<template>
    <b-modal
        id="personalgoal-tasks-overview-modal"
        v-model="modal"
        modal-class="apprentice-default-modal personal-goal-tasks-overview-modal"
        :title="`Taak: ${task.name} overzicht`"
        header-class="personal-goal-tasks-overview-header"
        :ok-title="okTitle"
        ok-variant="primary"
        :ok-only="teacherAccess === 1"
        @ok="onOk"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        size="lg"
        :no-enforce-focus="true">
        <div class="personal-goal-task-overview">
            <div
                class="errors"
                v-if="hasError">
                <b-alert
                    show
                    variant="danger">
                    Om een taak te kunnen afronden dien je eerst een reflectie en een bewijsstuk op te geven.
                </b-alert>
            </div>

            <div class="header">
                <div
                    class="deadline"
                    v-if="
                        task.personal_goal_task_deadline !== null &&
                        typeof task.personal_goal_task_deadline === 'object' &&
                        task.personal_goal_task_deadline.agenda_item !== null &&
                        typeof task.personal_goal_task_deadline.agenda_item === 'object'
                    ">
                    <span class="label">Deadline:</span>
                    <i class="icon far fa-calendar"></i>
                    <span class="date">
                        {{ task.personal_goal_task_deadline.agenda_item.date | date_named_month_short }}
                    </span>
                </div>
            </div>

            <div
                class="reflection"
                v-if="teacherAccess">
                <h5 class="title">Reflectie</h5>
                <div
                    class="not-found with-header"
                    v-if="task.reflection === '' || task.reflection === null">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">Er is geen reflectie, omdat de taak nog niet is afgerond.</div>
                </div>

                <div
                    class="preview"
                    v-else>
                    <design2021-apprentice-display-html-component v-html="task.reflection">
                    </design2021-apprentice-display-html-component>
                </div>
            </div>

            <b-form-group
                class="task-reflection-group"
                v-else
                label="Reflectie">
                <ckeditor
                    v-model="text"
                    :editor="editor"
                    :config="editorConfig">
                </ckeditor>
            </b-form-group>

            <b-button
                class="btn-upload btn-task-file-upload"
                v-if="!teacherAccess && attachments.length === 0"
                variant="primary"
                @click="$refs['task-reflection-file'].$el.childNodes[0].click()">
                Bewijsstuk selecteren
            </b-button>

            <div
                class="attachments-preview"
                v-if="attachments.length > 0">
                <h5 class="title">Bijlage</h5>
                <design-2021-apprentice-data-table
                    class="attachments-table"
                    :fields="fields"
                    :items="items"
                    nosearch>
                    <template #cell(media_files)="row">
                        <div class="file-name">
                            {{ row.item.media_files }}
                        </div>
                    </template>

                    <template #cell(type)="row">
                        <design-2021-apprentice-display-html-component
                            class="personal-goal-tasks-file-type"
                            :html-content="getMediaType(row)">
                        </design-2021-apprentice-display-html-component>
                    </template>

                    <template #cell(actions)="row">
                        <div class="attachments-management">
                            <b-button
                                class="btn-show attachments-file-open"
                                v-b-tooltip.hover
                                v-if="task.checked === 1"
                                variant="primary-icon-only"
                                title="Bijlage openen"
                                target="_blank"
                                :href="task.file.public_path">
                            </b-button>

                            <b-button
                                class="btn-remove attachments-file-remove"
                                v-if="!teacherAccess"
                                variant="danger-icon-only"
                                @click="removeMedia(row)">
                            </b-button>
                        </div>
                    </template>
                </design-2021-apprentice-data-table>
            </div>

            <b-file
                class="hidden"
                v-model="files"
                ref="task-reflection-file"
                @change="onFileChange">
            </b-file>
        </div>
    </b-modal>
</template>

<script>
import ApprenticeEditor from '../../../helpers/ApprenticeEditor';
import Design2021ApprenticeDisplayHtmlComponent from '../../common/design-2021-apprentice-display-html-component.vue';

export default {
    name: 'design-2021-personal-goal-task-overview-modal-component',
    components: { Design2021ApprenticeDisplayHtmlComponent },
    props: {
        teacherAccess: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            modal: false,
            task: {},
            text: '',
            files: [],
            attachments: [],
            fields: [
                {
                    key: 'media_files',
                    label: 'Bestand',
                },
                {
                    key: 'type',
                    label: 'Type',
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
            hasError: false,
            editor: ApprenticeEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'outdent',
                        'indent',
                        'alignment',
                        '|',
                        'fontColor',
                        'fontFamily',
                        'fontSize',
                        '|',
                        'undo',
                        'redo',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
        };
    },

    computed: {
        okTitle() {
            if (this.teacherAccess) {
                return 'Sluiten';
            } else if (this.task.checked) {
                return 'Aanpassen';
            } else {
                return 'Afronden';
            }
        },

        items() {
            if (this.attachments.length >= 1) {
                return this.attachments.map((file) => {
                    return {
                        media_files: file.name,
                        type: file.type === 'audio/mpeg' ? 'mp3' : file.type.split('/').slice(-1)[0],
                    };
                });
            }

            return [];
        },
    },

    methods: {
        show(task) {
            this.task = task;
            this.text = this.task.reflection !== null ? this.task.reflection : '';
            if (this.task.file_id !== null) {
                this.attachments = [this.task.file];
                this.attachments[0].type = this.task.file.extension;
            } else {
                this.attachments = [];
            }
            this.modal = true;
        },

        onFileChange(evt) {
            const localFiles = evt.target.files[0];
            this.attachments = [localFiles];
        },

        getMediaType(media) {
            const imageTypes = [
                'jpg',
                'JPG',
                'jpeg',
                'JPEG',
                'png',
                'PNG',
                'gif',
                'GIF',
                'tiff',
                'TIFF',
                'bmp',
                'BMP',
                'jif',
                'jfif',
            ];
            const videoTypes = ['mp4', 'mov', 'avi', 'webm', 'wmv', 'mkv', 'ogg', 'vob', 'mpeg', '3gp', 'mpeg'];

            if (videoTypes.includes(media.item.type)) {
                return `<span class="icon text-primary btn-video-icon"></span>Videobestand`;
            } else if (media.item.type === 'link') {
                return `<span class="icon text-primary btn-youtube-icon"></span>Video link`;
            } else if (imageTypes.includes(media.item.type)) {
                return `<span class="icon text-primary btn-image-icon"></span>Afbeelding`;
            } else if (media.item.type === 'mp3') {
                return `<span class="icon text-primary btn-audio-icon"></span>Audiobestand`;
            } else {
                return `<span class="icon text-primary btn-file-icon"></span>Bestand`;
            }
        },

        removeMedia(row) {
            this.attachments = this.attachments.filter((file, index) => index !== row.index);
        },

        onOk(bvModalEvent) {
            if (!this.$authUser.isStudent()) {
                this.$bvModal.hide('personalgoal-tasks-overview-modal');
                return;
            }

            if (!this.isTaskValidated()) {
                this.hasError = true;
                bvModalEvent.preventDefault();
                return;
            }

            const data = new FormData();
            const headers = { 'Content-Type': 'multipart/form-data' };

            data.append('personal_goal_id', this.task.personal_goal_id);
            data.append('reflection', this.text);

            if (this.attachments[0] instanceof File) {
                data.append('file', this.attachments[0]);
            }

            this.alert.saving();
            if (this.task.checked) {
                this.axios
                    .post(this.routes.personalGoalTaskStatusUpdate(this.task.id), data, { headers: headers })
                    .then(
                        (response) => {
                            this.alert.success();
                            this.$emit('update-task-status', response.data);
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
            } else {
                this.axios
                    .post(this.routes.personalGoalTaskStatusSubmit(this.task.id), data, { headers: headers })
                    .then(
                        (response) => {
                            this.alert.success();
                            this.$emit('update-task-status', response.data);
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
            }
        },

        isTaskValidated() {
            this.hasError = false;
            return this.attachments.length > 0 && this.text !== '' && this.text !== null;
        },
    },
};
</script>

<style scoped lang="scss">
.personal-goal-tasks-overview-modal ::v-deep {
    .modal-content {
        max-height: 900px;
        overflow-y: auto;
    }

    h5 {
        font-weight: $font-weight-medium;
    }
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
    column-gap: $grid-gutter-width;
}

.deadline {
    text-align: right;
    font-size: 1.3125rem;
    color: $danger;

    .label,
    .icon {
        margin-right: map-get($grid-gutter-spacing, 'eight');
    }
}

.btn-task-file-upload {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.attachments-preview:nth-child(3) {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
}

.file-name {
    color: $text-muted-color;
    font-weight: $font-weight-medium;
}

.personal-goal-tasks-file-type {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.attachments-management {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');
}
</style>
